// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  Eye, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  AlertCircle, Filter, Flag, Loader2, ShieldOff, AlertTriangle
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Sighting Categories
const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'ROBBERY', label: 'Robbery' },
  { value: 'RAM_RAID', label: 'Ram Raid' },
  { value: 'BURGLARY', label: 'Burglary' },
  { value: 'THEFT', label: 'Theft' },
  { value: 'DANGEROUS_DRIVING', label: 'Dangerous Driving' },
  { value: 'HOSTILE_RECON', label: 'Hostile Recon' },
  { value: 'CLONED_PLATES', label: 'Cloned Plates' },
  { value: 'OTHER', label: 'Other' }
];

// Threat Level Badge
function ThreatBadge({ level }) {
  const colors = {
    0: { bg: '#E8F5E9', text: '#2E7D32' },
    1: { bg: '#FFF8E1', text: '#F57F17' },
    2: { bg: '#FFF3E0', text: '#E65100' },
    3: { bg: '#FFEBEE', text: '#C62828' },
    4: { bg: '#FFCDD2', text: '#B71C1C' },
    5: { bg: '#B71C1C', text: '#FFFFFF' }
  };
  const config = colors[level] || colors[0];
  
  return (
    <span 
      className="px-2 py-1 rounded text-xs font-semibold"
      style={{ backgroundColor: config.bg, color: config.text }}
    >
      Level {level}
    </span>
  );
}

// Table Skeleton
function TableSkeleton() {
  return (
    <tbody className="divide-y divide-gray-100">
      {[...Array(5)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4 w-8"><div className="h-4 w-4 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-6 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
        </tr>
      ))}
    </tbody>
  );
}

// Expanded Row Details with Redact option
function ExpandedDetails({ sighting, onRedact, redactLoading }) {
  return (
    <tr className="bg-gray-50">
      <td colSpan="8" className="px-6 py-4">
        <div className="max-w-4xl">
          <div className="grid grid-cols-2 gap-6 mb-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Sighting Details</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Confidence:</span> <span className="font-medium">{sighting.confidence_level?.replace('_', ' ')}</span></p>
                <p><span className="text-gray-500">Police Incident #:</span> <span className="font-medium">{sighting.police_incident_number || 'N/A'}</span></p>
                <p><span className="text-gray-500">Created:</span> <span className="font-medium">{new Date(sighting.created_at).toLocaleString('en-GB')}</span></p>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase">Narrative</h4>
                {!sighting.narrative_redacted && sighting.narrative && (
                  <button
                    onClick={() => onRedact(sighting.id)}
                    disabled={redactLoading === sighting.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                    data-testid={`redact-narrative-${sighting.id}`}
                  >
                    {redactLoading === sighting.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ShieldOff className="w-3.5 h-3.5" />
                    )}
                    Redact Narrative
                  </button>
                )}
              </div>
              {sighting.narrative_redacted ? (
                <p className="text-sm text-red-600 italic bg-red-50 p-3 rounded-lg">
                  [REDACTED BY VCIN]
                </p>
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded-lg border border-gray-200">
                  {sighting.narrative || 'No narrative provided'}
                </p>
              )}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

// Redact Confirmation Modal
function RedactModal({ sighting, onClose, onConfirm, loading }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }
    onConfirm(sighting.id, reason);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-xl shadow-2xl z-50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <ShieldOff className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Redact Narrative</h2>
            <p className="text-sm text-gray-500">{sighting.vcin_reference}</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">This action is permanent:</p>
              <ul className="list-disc list-inside mt-1 text-amber-700">
                <li>The narrative will be replaced with "[REDACTED BY VCIN]"</li>
                <li>Original content cannot be recovered</li>
                <li>Action will be logged in audit trail</li>
              </ul>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason for Redaction *
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 resize-none"
            rows={3}
            placeholder="Enter the reason for redacting this narrative..."
            data-testid="redact-reason"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 h-10 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="confirm-redact-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
            Redact
          </button>
        </div>
      </div>
    </>
  );
}

export default function SASightingsPage() {
  const [sightings, setSightings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [expandedRow, setExpandedRow] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [redactModal, setRedactModal] = useState(null);
  const [redactLoading, setRedactLoading] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    company_id: '',
    category: '',
    threat_level: '',
    flagged: false,
    date_from: '',
    date_to: ''
  });

  const fetchSightings = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      
      if (filters.company_id) params.append('company_id', filters.company_id);
      if (filters.category) params.append('category', filters.category);
      if (filters.threat_level) params.append('threat_level', filters.threat_level);
      if (filters.flagged) params.append('flagged', 'true');
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const [sightingsRes, companiesRes] = await Promise.all([
        api.get(`/admin/sightings?${params.toString()}`),
        api.get('/admin/companies?limit=100')
      ]);
      
      setSightings(sightingsRes.data.sightings);
      setPagination(sightingsRes.data.pagination);
      setCompanies(companiesRes.data.companies);
    } catch (err) {
      setError(err.message || 'Failed to load sightings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSightings();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchSightings(1);
    setShowFilters(false);
  };

  const handleRedact = async (sightingId, reason) => {
    try {
      setRedactLoading(sightingId);
      await api.patch(`/admin/sightings/${sightingId}/redact`, { reason });
      setRedactModal(null);
      await fetchSightings(pagination.page);
    } catch (err) {
      alert(err.message || 'Failed to redact narrative');
    } finally {
      setRedactLoading(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  if (error && !loading) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="sa-sightings-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sightings</h1>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors border bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          style={activeFilterCount > 0 ? { backgroundColor: `${VCIN_COLORS.primary}10`, borderColor: VCIN_COLORS.primary, color: VCIN_COLORS.primary } : {}}
          data-testid="filter-btn"
        >
          <Filter className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <span 
              className="ml-1 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center"
              style={{ backgroundColor: VCIN_COLORS.primary }}
            >
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200" data-testid="filters-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <select
                value={filters.company_id}
                onChange={(e) => handleFilterChange('company_id', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Companies</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Threat Level</label>
              <select
                value={filters.threat_level}
                onChange={(e) => handleFilterChange('threat_level', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Levels</option>
                {[0, 1, 2, 3, 4, 5].map(level => (
                  <option key={level} value={level}>Level {level}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer h-10">
                <input
                  type="checkbox"
                  checked={filters.flagged}
                  onChange={(e) => handleFilterChange('flagged', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Flagged Only</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setFilters({ company_id: '', category: '', threat_level: '', flagged: false, date_from: '', date_to: '' })}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Reset
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg"
              style={{ backgroundColor: VCIN_COLORS.primary }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="sightings-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 w-8"></th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">VCIN Ref</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">VRN</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Threat</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Officer</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            
            {loading ? (
              <TableSkeleton />
            ) : sightings.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="8" className="p-12 text-center">
                    <Eye className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-600">No sightings found</p>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {sightings.map((sighting) => (
                  <React.Fragment key={sighting.id}>
                    <tr 
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                        expandedRow === sighting.id ? 'bg-gray-50' : ''
                      } ${sighting.flagged_for_review ? 'bg-amber-50/50' : ''}`}
                      onClick={() => toggleExpand(sighting.id)}
                    >
                      <td className="px-6 py-4 w-8">
                        <button className="p-1 hover:bg-gray-200 rounded">
                          {expandedRow === sighting.id ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium" style={{ color: VCIN_COLORS.primary }}>
                            {sighting.vcin_reference}
                          </span>
                          {sighting.flagged_for_review && (
                            <Flag className="w-4 h-4 text-amber-500 fill-amber-500" />
                          )}
                          {sighting.narrative_redacted && (
                            <ShieldOff className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span 
                          className="px-2 py-1 rounded text-sm font-bold"
                          style={{ backgroundColor: VCIN_COLORS.plateYellow, fontFamily: 'monospace' }}
                        >
                          {sighting.vrn}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {sighting.primary_category?.replace(/_/g, ' ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <ThreatBadge level={sighting.threat_level} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {sighting.company_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {sighting.officer_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(sighting.datetime).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                      </td>
                    </tr>
                    {expandedRow === sighting.id && (
                      <ExpandedDetails 
                        sighting={sighting} 
                        onRedact={(id) => setRedactModal(sighting)}
                        redactLoading={redactLoading}
                      />
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 20 + 1} to {Math.min(pagination.page * 20, pagination.total)} of {pagination.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchSightings(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => fetchSightings(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Redact Modal */}
      {redactModal && (
        <RedactModal
          sighting={redactModal}
          onClose={() => setRedactModal(null)}
          onConfirm={handleRedact}
          loading={redactLoading === redactModal.id}
        />
      )}
    </div>
  );
}
